// import React, { useState } from "react";
import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, useTheme, Paper, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../casting/ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { ForgingQuestions } from "./questions";
import { ForgingPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";

const DEFAULT_BREAKDOWN_ORDER = ["material", "process",  "ohp"];
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  ohp: "Overhead & Profits",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions = [],
  productData,
  formulaResult,
  perlColors,
  perlTypography,
}) {
  return (
    <Box>
      {assumptions && assumptions.length > 0 && (
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          {(() => {
            const bottomSectionKeywords = [
              "Machine tonnage:",
              "Tonnage:",
              "Cycle time:",
              "Cycle Time:",
              "Machine rate:",
              "OHP rate:",
              "Material price:",
              "Material Price:",
              "Labour cost:",
              "Labour Cost:",
              "Labour Rate:",
              "Forge Cycle Time:",
              "Heating Cycle Time:",
              "Blasting Required:",
            ];

            // Case-insensitive matching for keywords
            const topAssumptions = assumptions.filter((assumption) => {
              const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
              return !bottomSectionKeywords.some((keyword) =>
                assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
              );
            });
            const bottomAssumptions = assumptions.filter((assumption) => {
              const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
              return bottomSectionKeywords.some((keyword) =>
                assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
              );
            });

            // Build product information from productData if available
            // Only show selected values from productData, not assumptions
            const buildProductInfo = () => {
              if (!productData) {
                return [];
              }

              const productInfo = [];

              // Product Name
              if (productData.productName) {
                productInfo.push(`Product Name: ${productData.productName}`);
              }

              // Material
              if (productData.materialSelection) {
                const formattedMaterial =
                  typeof productData.materialSelection === "object"
                    ? productData.materialSelection.material
                    : productData.materialSelection;
                const grade =
                  typeof productData.materialSelection === "object"
                    ? productData.materialSelection.materialGrade
                    : productData.materialGrade;
                productInfo.push(
                  grade && grade !== ""
                    ? `Material: ${formattedMaterial} (Grade: ${grade})`
                    : `Material: ${formattedMaterial}`
                );
              }

              // Batch Size
              if (productData.batchSize) {
                productInfo.push(`Batch Size: ${productData.batchSize}`);
              }

              // Part Dimensions
              const dims = productData.partWeightDimensions;
              if (dims) {
                const dimParts = [];
                if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
                if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
                if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
                if (dims.finishWeight) dimParts.push(`Weight: ${dims.finishWeight}g`);
                if (dimParts.length > 0) {
                  productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
                }
              }

              // Part Details
              const partDetails = productData.partDetails || {};
              const detailParts = [];
              if (partDetails.avgWallThickness) {
                detailParts.push(`Wall: ${partDetails.avgWallThickness}mm`);
              }
              if (partDetails.noOfCavities) {
                detailParts.push(`Cavities: ${partDetails.noOfCavities}`);
              }
              if (detailParts.length > 0) {
                productInfo.push(`Part Details: ${detailParts.join(", ")}`);
              }

              // Manufacturing Country
              if (productData.manufacturingCountry) {
                productInfo.push(
                  `Manufacturing Country: ${productData.manufacturingCountry}`
                );
              }

              // Annual Volume (if available)
              if (productData.annualVolume) {
                productInfo.push(`Annual Volume: ${productData.annualVolume}`);
              }

              return productInfo;
            };

            const productInfoItems = buildProductInfo();

            return (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: perlColors.textPrimary,
                    }}
                  >
                    Product Information
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {productInfoItems.map((item, idx) => {
                      const itemStr = typeof item === 'string' 
                        ? item 
                        : (typeof item === 'object' && item !== null 
                            ? (item.value || item.name || item.reason || JSON.stringify(item))
                            : String(item || ''));
                      return (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {itemStr}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                {bottomAssumptions.length > 0 && (
                  <Box
                    sx={{
                      width: "1px",
                      bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
                      alignSelf: "stretch",
                    }}
                  />
                )}

                {bottomAssumptions.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        fontFamily: perlTypography.family,
                        letterSpacing: "0.3px",
                        color: perlColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {bottomAssumptions.map((assumption, idx) => {
                        const assumptionStr = typeof assumption === 'string' 
                          ? assumption 
                          : (typeof assumption === 'object' && assumption !== null 
                              ? (assumption.value || assumption.name || assumption.reason || JSON.stringify(assumption))
                              : String(assumption || ''));
                        return (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {assumptionStr}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
}

export default function ForgingPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [oldPlayBookData, setOldPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  const [demoMode, setDemoMode] = useState(false);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Debug: Log keyInsightsData changes
  useEffect(() => {
    console.log("🔍 keyInsightsData changed:", keyInsightsData);
    if (keyInsightsData) {
      console.log("   - Has cost_savings:", Array.isArray(keyInsightsData.cost_savings), "length:", keyInsightsData.cost_savings?.length);
      console.log("   - Has strategic:", Array.isArray(keyInsightsData.strategic), "length:", keyInsightsData.strategic?.length);
    }
  }, [keyInsightsData]);

  // Functional groups for formulation
  // const functionalGroups = [
  //   "Bulk ingredient",
  //   "Sweetners",
  //   "Oil and fats",
  //   "Active ingredient",
  //   "Leavening agents",
  //   "Flavour enhancers",
  //   "Flavours",
  //   "Colours",
  //   "Preservatives",
  //   "Emulsifiers",
  //   "Stabilizers",
  //   "Thickeners",
  //   "Acidulants",
  //   "Vitamins and minerals",
  //   "Other",
  // ];
  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  const handleDemoModeChange = (isDemoMode) => {
    setDemoMode(isDemoMode);
  };

  const handleProductReset = () => {
    setActiveSection("product");
    setProductData(null);
    setPlayBookData(null);
    setOldPlayBookData(null);
    setCostData(null);
    setRiskData(null);
    setSubstituteData(null);
    setProductImageUrl(null);
    setGeneratingImage(false);
    setKeyInsightsData(null);
    setSectionStarted((prev) => ({
      ...prev,
      playbook: false,
      results: false,
    }));
  };

  // Helper function to recalculate OHP cost when OHP Rate changes
  // const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
  //   if (!costData || !costData.breakdown || !costData._originalResult) {
  //     return costData;
  //   }

  //   const to4 = (n) => {
  //     if (n === null || n === undefined || n === '') return 0;
  //     if (typeof n === 'string') {
  //       const cleaned = n.replace(/[$,\s]/g, '').trim();
  //       const parsed = parseFloat(cleaned);
  //       return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
  //     }
  //     return Number(Number(n).toFixed(4));
  //   };

  //   // Get base material and process costs (these don't change)
  //   const material = Number(costData.breakdown.material || 0);
  //   const process = Number(costData.breakdown.process || 0);
  //   const baseOhpCost = Number(costData.meta?.baseOhpCost ?? costData.breakdown.ohp ?? 0);
  //   const baseOhpRate =
  //     Number(costData.meta?.baseOhpRate) ||
  //     (material + process > 0 ? (baseOhpCost / (material + process)) * 100 : 0);
  //   const baseFormulaCalc =
  //     material + process > 0 ? (material + process) * (baseOhpRate / 100) : 0;
  //   console.log(
  //     "🧮 Base OHP Formula:",
  //     `(${material} + ${process}) × (${baseOhpRate}/100) = ${baseFormulaCalc}`
  //   );

  //   // Recalculate OHP based on percentage change relative to original rate
  //   const targetOhpRate =
  //     typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate ?? 0);
  //   console.log(
  //     "➡️ Recalculating OHP:",
  //     `Current meta rate=${costData.meta?.currentOhpRate}, base rate=${baseOhpRate}`
  //   );

  //   let ohp = baseOhpCost;
  //   if (!isNaN(targetOhpRate)) {
  //     if (baseOhpRate > 0) {
  //       const rateRatio = targetOhpRate / baseOhpRate;
  //       const calc = baseOhpCost * rateRatio;
  //       console.log(
  //         "📐 OHP (based on ratio):",
  //         `(${baseOhpCost} * ${rateRatio}) = ${calc}`
  //       );
  //       ohp = to4(calc);
  //       } else {
  //       const ohpRateDecimal =
  //         targetOhpRate > 1 ? targetOhpRate / 100 : targetOhpRate;
  //       const calc = (material + process) * ohpRateDecimal;
  //       console.log(
  //         "📐 OHP (Material+Process * rate):",
  //         `(${material} + ${process}) * ${ohpRateDecimal} = ${calc}`
  //       );
  //       ohp = to4(calc);
  //     }
  //     const targetFormulaCalc =
  //       (material + process) * (targetOhpRate / 100);
  //     console.log(
  //       "🧮 Target OHP Formula:",
  //       `(${material} + ${process}) × (${targetOhpRate}/100) = ${targetFormulaCalc}`
  //     );
  //   }

  //   // Calculate new total: material + process + ohp
  //   const calculatedTotal = Number(material) + Number(process) + Number(ohp);
  //   const total = to4(calculatedTotal);

  //   // Update assumptions array with updated PlayBook values
  //   const resultData = costData._originalResult;
  //   const assumptionsArray = [];
    
  //   // Part Dimensions - from product data
  //   const dimensionData = productData?.partWeightDimensions || {};
  //   if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
  //     assumptionsArray.push(
  //       `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
  //     );
  //   }
    
  //   // Machine Tonnage
  //   if (updatedCostParams.machineTonnage) {
  //     assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
  //   } else if (resultData.Tonnage || resultData.cTonnage) {
  //     assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
  //   }
    
  //   // Cavities
  //   if (updatedCostParams.cavities) {
  //     assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
  //   } else if (resultData.cCavities || resultData.Cavities) {
  //     assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
  //   }
    
  //   // Runner Type
  //   if (updatedCostParams.runnerType && updatedCostParams.runnerType !== "N/A") {
  //     assumptionsArray.push(`Runner type: ${updatedCostParams.runnerType}`);
  //   } else if (resultData.RunnerType) {
  //     assumptionsArray.push(`Runner type: ${resultData.RunnerType}`);
  //   }
    
  //   // Cycle Time
  //   if (updatedCostParams.cycleTime) {
  //     assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
  //   } else if (resultData.CycleTime) {
  //     assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
  //   }
    
  //   // Machine Rate - always from stored procedure
  //   if (resultData.MHR) {
  //     const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
  //     if (!isNaN(machineRate)) {
  //       assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
  //     }
  //   }
    
  //   // Material Price - always from stored procedure
  //   if (resultData.RMPrice) {
  //     const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
  //     if (!isNaN(materialPrice)) {
  //       assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
  //     }
  //   }
    
  //   // Labour Cost - always from stored procedure
  //   if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
  //     const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
  //       ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
  //       : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
  //     if (!isNaN(labourCost)) {
  //       assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
  //     }
  //   }
    
  //   // OHP Rate - use updated value
  //   if (newOhpRate !== null && newOhpRate !== undefined && newOhpRate !== 0) {
  //     const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
  //     if (!isNaN(ohpRateValue)) {
  //       assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
  //     }
  //   }

  //   // Update cost data with new OHP, total, and assumptions
  //   return {
  //     ...costData,
  //     breakdown: {
  //       ...costData.breakdown,
  //       ohp: ohp,
  //       total_should_cost: total,
  //     },
  //     summary: costData.summary.replace(
  //       /\$[\d.]+ per unit/,
  //       `$${total} per unit`
  //     ),
  //     assumptions:
  //       assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
  //     meta: {
  //       ...costData.meta,
  //       currentOhpRate: targetOhpRate,
  //     },
  //   };
  // };

  // // Helper function to fetch material price and update material cost when tonnage changes
  // const updateMaterialCostFromTonnage = async (tonnage, updatedCostParams) => {
  //   console.log("\n" + "=".repeat(80));
  //   console.log("💰 updateMaterialCostFromTonnage called");
  //   console.log("=".repeat(80));
  //   console.log("📥 Input Parameters:");
  //   console.log("   - Tonnage:", tonnage);
  //   console.log("   - CostData exists:", !!costData);
  //   console.log("   - CostData source:", costData?._source);
  //   console.log("   - ProductData exists:", !!productData);

  //   if (!costData || costData._source !== "stored_procedure" || !productData) {
  //     console.log("⚠️ Early return: Missing costData or productData");
  //     return;
  //   }

  //   try {
  //     const manufacturingCountry = productData.manufacturingCountry || "";
  //     const materialName = productData.materialSelection || null;
  //     const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

  //     console.log("📋 Product Data:");
  //     console.log("   - Manufacturing Country:", manufacturingCountry);
  //     console.log("   - Material Name:", materialName);
  //     console.log("   - Part Weight (g):", partWeightG);

  //     if (!manufacturingCountry || !tonnage || partWeightG <= 0) {
  //       console.warn("⚠️ Cannot update material cost: missing country, tonnage, or part weight");
  //       console.log("   - Country:", manufacturingCountry || "MISSING");
  //       console.log("   - Tonnage:", tonnage || "MISSING");
  //       console.log("   - Part Weight:", partWeightG || "MISSING");
  //       return;
  //     }

  //     // Get current material cost before update
  //     const currentMaterialCost = costData.breakdown?.material || 0;
  //     const currentMaterialPricePerKg = costData.meta?.materialPricePerKg || null;
      
  //     console.log("\n📊 Current Values:");
  //     console.log("   - Current Material Cost:", `$${currentMaterialCost}/unit`);
  //     console.log("   - Current Material Price:", currentMaterialPricePerKg ? `$${currentMaterialPricePerKg}/kg` : "N/A");

  //     const requestPayload = {
  //       tonnage: tonnage,
  //       manufacturingCountry: manufacturingCountry,
  //       materialName: materialName,
  //     };

  //     console.log("\n📤 API Request Payload:");
  //     console.log(JSON.stringify(requestPayload, null, 2));
  //     console.log(`\n🔁 Fetching material price for ${tonnage} tons in ${manufacturingCountry}...`);

  //     const response = await fetch("/api/mech/material-price", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(requestPayload),
  //     });

  //     console.log("📥 API Response Status:", response.status, response.statusText);

  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
  //       console.error("❌ API Error Response:", errorData);
  //       throw new Error(errorData.error || "Failed to fetch material price");
  //     }

  //     const result = await response.json();
  //     console.log("\n📥 API Response Data:");
  //     console.log(JSON.stringify(result, null, 2));

  //     const newMaterialPricePerKg = parseFloat(result.material_price || 0);
  //     console.log("\n💰 Material Price Parsed:");
  //     console.log("   - Raw value:", result.material_price);
  //     console.log("   - Parsed value:", newMaterialPricePerKg);

  //     if (isNaN(newMaterialPricePerKg) || newMaterialPricePerKg <= 0) {
  //       console.warn("⚠️ Invalid material price received:", result);
  //       return;
  //     }

  //     // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
  //     // const partWeightKg = parseFloat(partWeightG) / 1000;
  //     // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
  //     // Update Material in cost category table with material price per kg directly
  //     const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

  //     console.log("\n🧮 Calculation:");
  //     console.log("   - Material Price ($/kg):", newMaterialPricePerKg);
  //     console.log("   - Using material price per kg directly as material cost");
  //     console.log("   - New Material Cost ($/unit):", newMaterialCost);

  //     // Update costData with new material cost
  //     const to4 = (n) => {
  //       if (n === null || n === undefined || n === '') return 0;
  //       if (typeof n === 'string') {
  //         const cleaned = n.replace(/[$,\s]/g, '').trim();
  //         const parsed = parseFloat(cleaned);
  //         return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
  //       }
  //       return Number(Number(n).toFixed(4));
  //     };

  //     const process = to4(costData.breakdown.process || 0);
  //     const ohp = to4(costData.breakdown.ohp || 0);
  //     const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

  //     console.log("\n📊 Cost Breakdown Update:");
  //     console.log("   - Material (old):", `$${currentMaterialCost}/unit`);
  //     console.log("   - Material (new):", `$${newMaterialCost}/unit`);
  //     console.log("   - Process:", `$${process}/unit`);
  //     console.log("   - OHP:", `$${ohp}/unit`);
  //     console.log("   - Total (old):", `$${costData.breakdown.total_should_cost}/unit`);
  //     console.log("   - Total (new):", `$${newTotal}/unit`);
  //     console.log("   - Total Formula:", `${newMaterialCost} + ${process} + ${ohp} = ${newTotal}`);

  //     const updatedCostData = {
  //       ...costData,
  //       breakdown: {
  //         ...costData.breakdown,
  //         material: newMaterialCost,
  //         total_should_cost: newTotal,
  //       },
  //       summary: costData.summary.replace(
  //         /\$[\d.]+ per unit/,
  //         `$${newTotal} per unit`
  //       ),
  //       assumptions: costData.assumptions.map((assumption) => {
  //         // Update material price in assumptions if it exists
  //         if (assumption.includes("Material price:")) {
  //           return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
  //         }
  //         return assumption;
  //       }),
  //       meta: {
  //         ...costData.meta,
  //         materialPricePerKg: newMaterialPricePerKg,
  //       },
  //     };

  //     setCostData(updatedCostData);
  //     console.log("\n✅ Material cost updated successfully!");
  //     console.log("   - New Material Price:", `$${newMaterialPricePerKg}/kg`);
  //     console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);
  //     console.log("   - New Total Cost:", `$${newTotal}/unit`);
  //     console.log("=".repeat(80) + "\n");
  //   } catch (error) {
  //     console.error("\n❌ Failed to update material cost:");
  //     console.error("   - Error type:", error.constructor.name);
  //     console.error("   - Error message:", error.message);
  //     console.error("   - Stack:", error.stack);
  //     console.log("=".repeat(80) + "\n");
  //     // Don't show alert to user, just log the error
  //   }
  // };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previousOhpRate = playBookData?.costParams?.ohpRate;
      const newOhpRate = value.ohpRate;
      const previousTonnage = playBookData?.costParams?.machineTonnage;
      const newTonnage = value.machineTonnage;
      
      setPlayBookData({
        ...playBookData,
        costParams: value,
      });
      setKeyInsightsData(null);

      // If OHP Rate changed and we have existing costData, recalculate OHP cost
      // if (costData && costData._source === "stored_procedure" && 
      //     newOhpRate !== undefined && newOhpRate !== null && 
      //     newOhpRate !== previousOhpRate) {
      //   const updatedCostData = recalculateOHPCost(newOhpRate, costData, value);
      //   setCostData(updatedCostData);
      // }

      // Note: Material price update is now handled when Quick Cost button is clicked
      // No automatic update here - wait for user to click Quick Cost button
    } else {
    setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  // const analyzeCostWithData = async (recipe, playbook, product) => {
  //   try {
  //     console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

  //     const payload = {
  //       recipe: recipe,
  //       playBookData: playbook,
  //       productData: product,
  //     };

  //     // Call all three APIs in parallel
  //     const [costResponse, riskResponse, substituteResponse] = await Promise.all([
  //       fetch("/api/fnb/analyze-cost", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }),
  //       fetch("/api/fnb/analyze-risk", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }),
  //       fetch("/api/fnb/find-substitutes", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }),
  //     ]);

  //     // Check responses
  //     if (!costResponse.ok) {
  //       const errorData = await costResponse
  //         .json()
  //         .catch(() => ({ error: "Cost analysis failed" }));
  //       throw new Error(errorData.error || "Failed to analyze cost");
  //     }

  //     // Parse all responses (non-blocking for risk and substitute)
  //     const costResult = await costResponse.json();
  //     console.log("✅ Cost analysis complete:", costResult);
  //     setCostData(costResult);

  //     // Handle risk analysis
  //     if (riskResponse.ok) {
  //       const riskResult = await riskResponse.json();
  //       console.log("✅ Risk analysis complete:", riskResult);
  //       setRiskData(riskResult);
  //     } else {
  //       console.warn("⚠️ Risk analysis failed, continuing without risk data");
  //       setRiskData(null);
  //     }

  //     // Handle substitute analysis
  //     if (substituteResponse.ok) {
  //       const substituteResult = await substituteResponse.json();
  //       console.log("✅ Substitute analysis complete:", substituteResult);
  //       setSubstituteData(substituteResult);
  //     } else {
  //       console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
  //       setSubstituteData(null);
  //     }

  //     // Generate product image with the playbook data
  //     await generateProductImageWithData(product, playbook);

  //     // Navigate to results section and mark as started
  //     setActiveSection("results");
  //     setSectionStarted((prev) => ({ ...prev, results: true }));

  //     return costResult;
  //   } catch (error) {
  //     console.error("Analysis failed:", error);
  //     alert(`Failed to complete analysis: ${error.message}`);
  //     throw error;
  //   }
  // };

  // const handleAnalyzeCost = async () => {
  //   if (!recipeData || !playBookData || !productData) return;

  //   setLoadingMessage("Running cost analysis...");
  //   setIsProcessing(true);
  //   try {
  //     await analyzeCostWithData(recipeData, playBookData, productData);
  //   } finally {
  //     setLoadingMessage("");
  //     setIsProcessing(false);
  //   }
  // };

  // const generateProductImageWithData = async (product, playbook) => {
  //   if (!product) return;

  //   setGeneratingImage(true);

  //   try {
  //     const imagePayload = {
  //       productName: product.productName,
  //       companyName: "Kearney",
  //       brandName: "PERLab",
  //       commodity: "Plastics Molding",
  //       primaryPackaging: playbook?.primary_packaging || "",
  //       skuWt: product.skuWeight || "",
  //       skuWtUnit: product.skuWeightUnit || "g",
  //       marketPositioning: product.marketPositioning || "",
  //       style: "professional",
  //     };

  //     console.log("🖼️ Generating product image:", imagePayload);

  //     const response = await fetch("/api/ai/generate-product-image", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(imagePayload),
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.error || "Failed to generate image");
  //     }

  //     const data = await response.json();
  //     setProductImageUrl(data.image_url);
  //     console.log("✅ Product image generated:", data.image_url);
  //   } catch (error) {
  //     console.error("❌ Error generating product image:", error);
  //     // Don't alert - image is optional
  //   } finally {
  //     setGeneratingImage(false);
  //   }
  // };

  // const generateProductImage = () => {
  //   return generateProductImageWithData(productData, playBookData);
  // };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const imagePayload = {
        productName: product.productName || "",
        materialSelection:
          product.materialSelection?.material ||
          product.materialSelection?.name ||
          product.materialSelection ||
          "",
        processSelection: product.processSelection || "",
        partWeightDimensions: product.partWeightDimensions || {},
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Forging",
        primaryPackaging: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        style: "professional",
      };

      console.log("Generating product image:", imagePayload);

      // const response = await apiFetch("/api/ai/generate-product-image", {
      const response = await apiFetch("/api/mech/forging/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("Product image generated:", data.image_url);
    } catch (error) {
      console.error("Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook, costBreakdownOverride = null) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Mechanical",
      costModel: costBreakdownOverride || costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [{ ingredient: product?.materialSelection || "Forging Material", percentage: 100, functional_group: "Base" }];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "forging part"} based on available costing inputs.`,
      top_concerns: [
        "Raw material price volatility may impact should-cost.",
        "Forge/heating cycle variation may reduce throughput.",
        "Capacity and quality drift can affect delivery performance.",
      ],
      risk_items: [
        { title: "Material Cost Volatility", severity: 6, probability: 6, detail: "Track alloy/steel index and contract coverage." },
        { title: "Process Stability Risk", severity: 5, probability: 5, detail: "Monitor cycle, rework, and scrap rate trends." },
      ],
      assumptions: "Generated from forging product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const params = playbook?.costParams || {};
      const material = product?.materialSelection || "Current material";
      const cycleTime = params.cycleTime || "Current cycle time";
      const tonnage = params.machineTonnage || "Current tonnage";
      const ohpRate = params.ohpRate || "Current OHP rate";

      return {
        summary: `Forging optimization recommendations generated for ${product?.productName || "the current part"} using provided inputs.`,
        substitutes: [
          {
            current_item: `Material: ${material}`,
            proposed_item: "Validate equivalent lower-cost grade/spec",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires validation",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Validate mechanical properties and downstream process fit.",
            validation_steps: ["Material equivalence review", "Pilot run", "Mechanical/property testing"],
          },
          {
            current_item: `Cycle and tonnage: ${cycleTime}, ${tonnage}`,
            proposed_item: "Tune forging/heating cycle and machine settings",
            estimated_cost_delta_per_unit: "Potential conversion cost reduction",
            impact_on_quality: "Process-dependent",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Monitor dimensional and metallurgical consistency.",
            validation_steps: ["Time study", "DOE on settings", "Capability verification"],
          },
          {
            current_item: `Overhead assumption: ${ohpRate}`,
            proposed_item: "Optimize setup planning and line utilization",
            estimated_cost_delta_per_unit: "Potential OHP absorption improvement",
            impact_on_quality: "Low",
            feasibility_score: 7,
            timeline_weeks: 2,
            risk_note: "Ensure scheduling changes do not increase WIP delays.",
            validation_steps: ["Shift/load balancing", "Downtime analysis", "KPI monitoring"],
          },
        ],
        prioritization: [
          "Cycle/setup optimization for immediate throughput gains",
          "Utilization/OHP improvement actions",
          "Material equivalence validation for medium-term savings",
        ],
        assumptions: "Generated from forging product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/forging/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/forging/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!riskResponse.ok || !substituteResponse.ok) {
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        setRiskData(await riskResponse.json());
      } else {
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        setSubstituteData(await substituteResponse.json());
      } else {
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      console.log("⚠️ fetchKeyInsights: No product data provided");
      return;
    }
    try {
      console.log("🔍 Fetching key insights for forging...");
      const response = await apiFetch("/api/mech/forging/key-insights-forging", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      
      console.log("📥 Key insights response status:", response.status, response.statusText);
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `Failed to generate key insights (${response.status} ${response.statusText})`;
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
            console.error("❌ Key insights API error (JSON):", errorData);
          } else {
            const errorText = await response.text();
            if (errorText && errorText.trim()) {
              // Try to parse as JSON if it looks like JSON
              try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error || errorJson.message || errorMessage;
                console.error("❌ Key insights API error (parsed text):", errorJson);
              } catch {
                errorMessage = errorText || errorMessage;
                console.error("❌ Key insights API error (text):", errorText);
              }
            } else {
              console.error("❌ Key insights API error: Empty response body");
            }
          }
        } catch (parseError) {
          console.error("❌ Failed to parse error response:", parseError);
          // Use default error message if parsing fails
        }
        throw new Error(errorMessage);
      }
      
      // Check content type before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.warn("⚠️ Non-JSON response from key insights endpoint");
        const text = await response.text();
        console.warn("Response text:", text.substring(0, 200));
        throw new Error("Invalid response format from key insights API");
      }
      
      const insights = await response.json();
      console.log("📊 Raw key insights response:", insights);
      
      // Normalize the response to ensure it has the expected structure
      const normalizedInsights = {
        summary: insights.summary || "",
        cost_savings: Array.isArray(insights.cost_savings) 
          ? insights.cost_savings 
          : (insights.cost_savings ? [insights.cost_savings] : []),
        strategic: Array.isArray(insights.strategic) 
          ? insights.strategic 
          : (insights.strategic ? [insights.strategic] : []),
      };
      
      console.log("✅ Normalized key insights:", normalizedInsights);
      console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
      console.log("   - Strategic count:", normalizedInsights.strategic.length);
      
      setKeyInsightsData(normalizedInsights);
    } catch (error) {
      console.error("❌ Failed to fetch key insights:", error);
      // Set empty structure on error to prevent undefined issues
      setKeyInsightsData({
        summary: "",
        cost_savings: [],
        strategic: [],
      });
    }
  };

  // // Call stored procedure QC_SP_Plastics
  // Call forging costing calculation with SSE streaming
  const callForgingCostingStream = async (productData, playBookData) => {
    try {
      const timestamp = Date.now();
      
      // Prepare request payload matching backend expectations
      const requestData = {
        productData: productData,  // Send full productData object
        userEmail: user?.email || "",
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        // Include playbook if available
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling forging streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/mech/forging/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📥 SSE Event:", eventData);

            if (eventData.type === "complete") {
              // Analysis complete - return the final response
              console.log("✅ Analysis complete:", eventData);
              return eventData;
            } else if (eventData.type === "error") {
              throw new Error(eventData.message || "Analysis failed");
            }
            // Ignore progress events for now (can add progress tracking later)
          } catch (parseErr) {
            console.warn("Failed to parse SSE event:", parseErr);
          }
        }
      }

      throw new Error("Stream ended without complete event");
    } catch (err) {
      console.error("❌ Forging streaming call failed:", err);
      throw err;
    }
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis with SSE...");

    // Initialize progress steps immediately
    const totalSteps = 6;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setRiskData(null);
    setSubstituteData(null);

    // Track maximum progress to prevent going backwards
    let maxProgress = 0;

    try {
      const timestamp = Date.now();
      
      // Prepare request payload matching backend expectations
      const requestData = {
        productData: productData,
        userEmail: user?.email || "",
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling forging streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/mech/forging/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;

          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📥 SSE Event:", eventData);

            if (eventData.type === "start") {
              const totalSteps = eventData.total_steps || 6;
              if (totalSteps !== progressSteps.length) {
                const updatedSteps = Array.from({ length: totalSteps }, (_, i) => ({
                  id: `step-${i + 1}`,
                  label: `Step ${i + 1}`,
                  status: "pending",
                  message: "",
                }));
                setProgressSteps(updatedSteps);
              }
            } else if (eventData.type === "progress") {
              const stepIndex = eventData.step - 1;
              console.log(`📍 Progress Event - Step ${eventData.step}: ${eventData.label} (${eventData.status})`);

              setProgressSteps((prev) => {
                const updated = [...prev];
                if (updated[stepIndex]) {
                  let mappedStatus = eventData.status;
                  if (eventData.status === "running") {
                    mappedStatus = "in-progress";
                  } else if (eventData.status === "success") {
                    mappedStatus = "completed";
                  }

                  updated[stepIndex] = {
                    ...updated[stepIndex],
                    label: eventData.label || updated[stepIndex].label,
                    status: mappedStatus,
                    message: eventData.message || (eventData.status === "running" ? "Processing..." : "Complete"),
                    error: eventData.error,
                  };

                  console.log(`   ✓ Updated step ${eventData.step}: label="${updated[stepIndex].label}", status="${updated[stepIndex].status}"`);
                }
                return updated;
              });

              if (eventData.status === "running" || eventData.status === "success") {
                setCurrentProgressStep(eventData.step);
              }

              const newProgress = (eventData.step / eventData.total) * 100;
              if (newProgress > maxProgress) {
                maxProgress = newProgress;
                setOverallProgress(newProgress);
              }
            } else if (eventData.type === "complete") {
              console.log("✅ Analysis complete:", eventData);

              maxProgress = 100;
              setOverallProgress(100);

              setProgressSteps((prev) =>
                prev.map((step) => ({
                  ...step,
                  status: "completed",
                  message: "Complete",
                }))
              );

              // Handle the complete event data
              if (eventData && eventData.status === 'success') {
                const shouldCost = eventData.ShouldCostResult || {};
                const material = parseFloat(shouldCost.MaterialCost || 0);
                const process = parseFloat(shouldCost.ProcessCost || 0);
                const ohp = parseFloat(shouldCost.OHPCost || 0);
                const total = parseFloat(shouldCost.TotalCost || 0);

                let assumptionsArray = [];
                if (eventData.AssumptionsJson) {
                  const assumptions = eventData.AssumptionsJson;
                  if (Array.isArray(assumptions)) {
                    assumptionsArray = assumptions;
                  } else if (typeof assumptions === 'object') {
                    Object.values(assumptions).forEach((section) => {
                      if (Array.isArray(section)) {
                        assumptionsArray.push(...section);
                      }
                    });
                  }
                }

                if (assumptionsArray.length === 0 && productData) {
                  const dimensionData = productData?.partWeightDimensions || {};
                  if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
                    assumptionsArray.push(
                      `Part dimensions: weight ${dimensionData.finishWeight}g, L ${dimensionData.partLength}mm, W ${dimensionData.partWidth}mm, H ${dimensionData.partHeight}mm`
                    );
                  }
                  if (productData.materialSelection) {
                    const formattedMaterial = typeof productData.materialSelection === "object"
                      ? productData.materialSelection.material
                      : productData.materialSelection;
                    const grade = typeof productData.materialSelection === "object"
                      ? productData.materialSelection.materialGrade
                      : productData.materialGrade;
                    assumptionsArray.push(
                      grade && grade !== ""
                        ? `Material: ${formattedMaterial} (Grade: ${grade})`
                        : `Material: ${formattedMaterial}`
                    );
                  }
                  if (productData.processSelection) {
                    assumptionsArray.push(`Process: ${productData.processSelection}`);
                  }
                  if (productData.manufacturingCountry) {
                    assumptionsArray.push(`Manufacturing Country: ${productData.manufacturingCountry}`);
                  }
                }

                const assumptionsJson = eventData.AssumptionsJson;
                
                // Material Price - check multiple sources
                let materialPrice = null;
                if (shouldCost.MaterialPrice) {
                  materialPrice = shouldCost.MaterialPrice;
                } else if (assumptionsJson?.missing_inputs_filled?.steel_raw_material_usd_per_kg) {
                  materialPrice = assumptionsJson.missing_inputs_filled.steel_raw_material_usd_per_kg;
                } else if (assumptionsJson?.material_properties?.steel_raw_material_usd_per_kg) {
                  materialPrice = assumptionsJson.material_properties.steel_raw_material_usd_per_kg;
                }
                
                if (materialPrice) {
                  assumptionsArray.push(`Material Price: $${materialPrice}/kg`);
                }
                
                if (shouldCost.MHR) {
                  if(shouldCost.MHRSource == 'PERL'){
                    assumptionsArray.push(`Machine rate (${shouldCost.MHR_MachineTonnage} ton machine): $${shouldCost.MHR}/hour 🟣`);
                  } else {
                    assumptionsArray.push(`Machine rate: $${shouldCost.MHR}/hour`);
                  }
                }

                // Labour Cost - check multiple sources
                let labourCost = null;
                if (shouldCost.LHR) {
                  labourCost = shouldCost.LHR;
                } else if (assumptionsJson?.labour_cost?.labour_usd_per_hour) {
                  labourCost = assumptionsJson.labour_cost.labour_usd_per_hour;
                }
                
                if (labourCost) {
                  if(shouldCost.LHRSource == 'PERL' || shouldCost.MHRSource == 'PERL'){
                    assumptionsArray.push(`Labour Cost: $${labourCost}/hour 🟣`);
                  } else {
                    assumptionsArray.push(`Labour Cost: $${labourCost}/hour`);
                  }
                }

                // Process Parameters - forge cycle, heating cycle, blasting
                if (assumptionsJson?.process_parameters?.forge_cycle_sec) {
                  assumptionsArray.push(`Forge Cycle Time: ${assumptionsJson.process_parameters.forge_cycle_sec} sec`);
                }
                
                if (assumptionsJson?.process_parameters?.heating_cycle_sec) {
                  assumptionsArray.push(`Heating Cycle Time: ${assumptionsJson.process_parameters.heating_cycle_sec} sec`);
                }
                
                if (assumptionsJson?.process_parameters?.blasting_required !== undefined) {
                  assumptionsArray.push(`Blasting Required: ${assumptionsJson.process_parameters.blasting_required ? 'Yes' : 'No'}`);
                }

                const transformedCostData = {
                  summary: `Should cost analysis for ${productData.productName || "forging part"} manufactured in ${productData.manufacturingCountry || "location"}. Total cost: $${total.toFixed(4)} per unit.`,
                  breakdown: {
                    material: material,
                    process: process,
                    ohp: ohp,
                    total_should_cost: total,
                    currency: "USD/unit",
                  },
                  line_items: [],
                  key_insights: [],
                  assumptions: assumptionsArray,
                  _source: "forging_routes",
                  _originalResult: eventData,
                  formulaResult: eventData.formulaResult,
                };

                setCostData(transformedCostData);

                // Initialize PlayBook data
                const formulaResult = eventData.formulaResult;
                const playbookDataFromResponse = eventData.Playbook_Data;
                
                const parseNumericValue = (value, defaultValue = 0) => {
                  if (value === null || value === undefined || value === "") return defaultValue;
                  if (typeof value === "number") return isNaN(value) ? defaultValue : value;
                  const parsed = parseFloat(value);
                  return isNaN(parsed) ? defaultValue : parsed;
                };

                let cycleTime = 0;
                let machineTonnage = 0;
                let ohpRate = 0;
                
                if (playbookDataFromResponse?.costParams) {
                  cycleTime = parseNumericValue(playbookDataFromResponse.costParams.cycleTime, 0);
                  machineTonnage = parseNumericValue(playbookDataFromResponse.costParams.machineTonnage, 0);
                  ohpRate = parseNumericValue(playbookDataFromResponse.costParams.ohpRate, 0);
                }
                
                if (cycleTime === 0 && assumptionsJson?.process_parameters) {
                  cycleTime = parseNumericValue(assumptionsJson.process_parameters.total_cycle_time_sec || assumptionsJson.process_parameters.cycle_time || assumptionsJson.process_parameters.cycleTime, 0);
                }
                
                if (machineTonnage === 0 && assumptionsJson?.process_parameters) {
                  machineTonnage = parseNumericValue(assumptionsJson.process_parameters.total_installed_tonnage_ton || assumptionsJson.process_parameters.machine_tonnage || assumptionsJson.process_parameters.tonnage, 0);
                }
                
                if (formulaResult) {
                  if (cycleTime === 0) {
                    cycleTime = formulaResult.total_cycle_time_sec || formulaResult.totalCycleTimeSec || formulaResult.CycleTime || formulaResult.cycleTime || 0;
                  }
                  if (machineTonnage === 0) {
                    machineTonnage = formulaResult.total_installed_tonnage_ton || formulaResult.totalInstalledTonnageTon || formulaResult.MachineTonnage || formulaResult.machineTonnage || formulaResult.Tonnage || 0;
                  }
                }
                
                if (ohpRate === 0) {
                  if (assumptionsJson?.overhead_profit?.factory_overhead_percent !== undefined) {
                    ohpRate = parseNumericValue(assumptionsJson.overhead_profit.factory_overhead_percent, 0);
                  } else if (assumptionsJson?.ohpRate !== undefined) {
                    ohpRate = parseNumericValue(assumptionsJson.ohpRate, 0);
                    if (ohpRate < 1 && ohpRate > 0) ohpRate = ohpRate * 100;
                  } else if (formulaResult?.ohpRate !== undefined) {
                    ohpRate = parseNumericValue(formulaResult.ohpRate, 0);
                    if (ohpRate < 1 && ohpRate > 0) ohpRate = ohpRate * 100;
                  }
                }

                const existingCostParams = playBookData?.costParams || {};
                const isFirstRun = !playBookData || !playBookData.costParams;
                
                const playbookFromResult = {
                  costParams: {
                    cycleTime: (isFirstRun || existingCostParams.cycleTime === 0) 
                      ? parseNumericValue(cycleTime, 0) 
                      : existingCostParams.cycleTime,
                    machineTonnage: (isFirstRun || existingCostParams.machineTonnage === 0) 
                      ? parseNumericValue(machineTonnage, 0) 
                      : existingCostParams.machineTonnage,
                    ohpRate: (isFirstRun || existingCostParams.ohpRate === 0) 
                      ? parseNumericValue(ohpRate, 0) 
                      : existingCostParams.ohpRate,
                  },
                };
                
                setPlayBookData(playbookFromResult);
                setOldPlayBookData(playbookFromResult);

                if (eventData.riskData) {
                  setRiskData(eventData.riskData);
                }
                if (eventData.substituteData) {
                  setSubstituteData(eventData.substituteData);
                }
                if (!eventData.riskData || !eventData.substituteData) {
                  await fetchRiskAndSubstituteData(
                    productData,
                    playbookFromResult || playBookData,
                    transformedCostData.breakdown
                  );
                }

                // Close dialog and navigate to results first
                setShowProgressTracker(false);
                setIsProcessing(false);
                setActiveSection("results");
                setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

                // Extract keyInsights from streaming response if available, otherwise fetch separately
                const insights = eventData.keyInsights || null;
                if (insights) {
                  console.log("✅ Key insights from streaming response:", insights);
                  // Normalize the insights to ensure proper array structure
                  const normalizedInsights = {
                    summary: insights.summary || "",
                    cost_savings: Array.isArray(insights.cost_savings) 
                      ? insights.cost_savings 
                      : (insights.cost_savings ? [insights.cost_savings] : []),
                    strategic: Array.isArray(insights.strategic) 
                      ? insights.strategic 
                      : (insights.strategic ? [insights.strategic] : []),
                  };
                  console.log("📊 Normalized key insights:", normalizedInsights);
                  console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
                  console.log("   - Strategic count:", normalizedInsights.strategic.length);
                  setKeyInsightsData(normalizedInsights);
                } else if (eventData.KeyInsightsJson) {
                  const unwrappedKeyInsights = eventData.KeyInsightsJson.key_insights || eventData.KeyInsightsJson;
                  console.log("✅ Key insights from KeyInsightsJson:", unwrappedKeyInsights);
                  // Normalize the insights to ensure proper array structure
                  const normalizedInsights = {
                    summary: unwrappedKeyInsights.summary || "",
                    cost_savings: Array.isArray(unwrappedKeyInsights.cost_savings) 
                      ? unwrappedKeyInsights.cost_savings 
                      : (unwrappedKeyInsights.cost_savings ? [unwrappedKeyInsights.cost_savings] : []),
                    strategic: Array.isArray(unwrappedKeyInsights.strategic) 
                      ? unwrappedKeyInsights.strategic 
                      : (unwrappedKeyInsights.strategic ? [unwrappedKeyInsights.strategic] : []),
                  };
                  console.log("📊 Normalized key insights:", normalizedInsights);
                  console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
                  console.log("   - Strategic count:", normalizedInsights.strategic.length);
                  setKeyInsightsData(normalizedInsights);
                } else {
                  // Fallback: fetch key insights separately if not in streaming response
                  console.log("⚠️ No key insights in streaming response, fetching separately...");
                  await fetchKeyInsights(productData, playbookFromResult || {});
                }
                // Trigger image generation after Quick Cost completes (non-blocking)
                generateProductImageWithData(productData, playbookFromResult || playBookData);
              } else {
                throw new Error(eventData.message || "Forging costing failed");
              }

              break;
            } else if (eventData.type === "error") {
              throw new Error(eventData.error || eventData.message || "Analysis failed");
            }
          } catch (parseError) {
            console.error("❌ Error parsing SSE event:", parseError);
          }
        }
      }

      console.log("✅ Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("❌ Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const handleQuickCost = async () => {
    const Ai_Maintenance = "Schema Management V2";

    if (Ai_Maintenance === "Schema Management V2") {
      handleProgressiveQuickCost();
      return;
    }

    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);
    setKeyInsightsData(null);
    try {
      // Call forging costing calculation with streaming
      console.log("📞 Calling forging costing with streaming:", { productData, playBookData });

      const costResult = await callForgingCostingStream(productData, playBookData);

      // Handle the response from streaming endpoint
      if (costResult && costResult.status === 'success') {
        console.log("✅ Forging costing completed:", costResult);
        
        // Extract should cost result
        const shouldCost = costResult.ShouldCostResult || {};
        const material = parseFloat(shouldCost.MaterialCost || 0);
        const process = parseFloat(shouldCost.ProcessCost || 0);
        const ohp = parseFloat(shouldCost.OHPCost || 0);
        const total = parseFloat(shouldCost.TotalCost || 0);

        // Build assumptions array from response
        let assumptionsArray = [];
        if (costResult.AssumptionsJson) {
          // Extract assumptions from AssumptionsJson if available
          const assumptions = costResult.AssumptionsJson;
          if (Array.isArray(assumptions)) {
            assumptionsArray = assumptions;
          } else if (typeof assumptions === 'object') {
            // Flatten assumptions object into array
            Object.values(assumptions).forEach((section) => {
              if (Array.isArray(section)) {
                assumptionsArray.push(...section);
              }
            });
          }
        }

        // Build assumptions from product data if not available
        if (assumptionsArray.length === 0 && productData) {
          const dimensionData = productData?.partWeightDimensions || {};
          if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
            assumptionsArray.push(
              `Part dimensions: weight ${dimensionData.finishWeight}g, L ${dimensionData.partLength}mm, W ${dimensionData.partWidth}mm, H ${dimensionData.partHeight}mm`
            );
          }
          if (productData.materialSelection) {
            const formattedMaterial = typeof productData.materialSelection === "object"
              ? productData.materialSelection.material
              : productData.materialSelection;
            const grade = typeof productData.materialSelection === "object"
              ? productData.materialSelection.materialGrade
              : productData.materialGrade;
            assumptionsArray.push(
              grade && grade !== ""
                ? `Material: ${formattedMaterial} (Grade: ${grade})`
                : `Material: ${formattedMaterial}`
            );
          }
          if (productData.processSelection) {
            assumptionsArray.push(`Process: ${productData.processSelection}`);
          }
          if (productData.manufacturingCountry) {
            assumptionsArray.push(`Manufacturing Country: ${productData.manufacturingCountry}`);
          }
        }

        // Extract additional assumptions from AssumptionsJson
        const assumptionsJson = costResult.AssumptionsJson;
        
        // Material Price - check multiple sources
        let materialPrice = null;
        if (shouldCost.MaterialPrice) {
          materialPrice = shouldCost.MaterialPrice;
        } else if (assumptionsJson?.missing_inputs_filled?.steel_raw_material_usd_per_kg) {
          materialPrice = assumptionsJson.missing_inputs_filled.steel_raw_material_usd_per_kg;
        } else if (assumptionsJson?.material_properties?.steel_raw_material_usd_per_kg) {
          materialPrice = assumptionsJson.material_properties.steel_raw_material_usd_per_kg;
        }
        
        if (materialPrice) {
          assumptionsArray.push(`Material Price: $${materialPrice}/kg`);
        }
        
        // Machine Rate
        if (shouldCost.MHR) {
          if(shouldCost.MHRSource == 'PERL'){
            assumptionsArray.push(`Machine rate (${shouldCost.MHR_MachineTonnage} ton machine): $${shouldCost.MHR}/hour 🟣`);
          } else {
            assumptionsArray.push(`Machine rate: $${shouldCost.MHR}/hour`);
          }
        }

        // Labour Cost - check multiple sources
        let labourCost = null;
        if (shouldCost.LHR) {
          labourCost = shouldCost.LHR;
        } else if (assumptionsJson?.labour_cost?.labour_usd_per_hour) {
          labourCost = assumptionsJson.labour_cost.labour_usd_per_hour;
        }
        
        if (labourCost) {
          if(shouldCost.LHRSource == 'PERL' || shouldCost.MHRSource == 'PERL'){
            assumptionsArray.push(`Labour Cost: $${labourCost}/hour 🟣`);
          } else {
            assumptionsArray.push(`Labour Cost: $${labourCost}/hour`);
          }
        }

        // Process Parameters - forge cycle, heating cycle, blasting
        if (assumptionsJson?.process_parameters?.forge_cycle_sec) {
          assumptionsArray.push(`Forge Cycle Time: ${assumptionsJson.process_parameters.forge_cycle_sec} sec`);
        }
        
        if (assumptionsJson?.process_parameters?.heating_cycle_sec) {
          assumptionsArray.push(`Heating Cycle Time: ${assumptionsJson.process_parameters.heating_cycle_sec} sec`);
        }
        
        if (assumptionsJson?.process_parameters?.blasting_required !== undefined) {
          assumptionsArray.push(`Blasting Required: ${assumptionsJson.process_parameters.blasting_required ? 'Yes' : 'No'}`);
        }

        // Transform to format expected by ResultsSection
        const transformedCostData = {
          summary: `Should cost analysis for ${productData.productName || "forging part"} manufactured in ${productData.manufacturingCountry || "location"}. Total cost: $${total.toFixed(4)} per unit.`,
          breakdown: {
            material: material,
            process: process,
            ohp: ohp,
            total_should_cost: total,
            currency: "USD/unit",
          },
          line_items: [],
          key_insights: [],
          assumptions: assumptionsArray,
          _source: "forging_routes",
          _originalResult: costResult,
          formulaResult: costResult.formulaResult, // Store formula result for assumptions mapping
        };

        setCostData(transformedCostData);

        // Initialize PlayBook data from Playbook_Data, AssumptionsJson, and formulaResult
        const formulaResult = costResult.formulaResult;
        const playbookDataFromResponse = costResult.Playbook_Data;
        
        // Helper function to safely parse numeric values
        const parseNumericValue = (value, defaultValue = 0) => {
          if (value === null || value === undefined || value === "") return defaultValue;
          if (typeof value === "number") return isNaN(value) ? defaultValue : value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? defaultValue : parsed;
        };

        // Extract values from Playbook_Data (calculated values from backend)
        let cycleTime = 0;
        let machineTonnage = 0;
        let ohpRate = 0;
        
        if (playbookDataFromResponse?.costParams) {
          cycleTime = parseNumericValue(playbookDataFromResponse.costParams.cycleTime, 0);
          machineTonnage = parseNumericValue(playbookDataFromResponse.costParams.machineTonnage, 0);
          ohpRate = parseNumericValue(playbookDataFromResponse.costParams.ohpRate, 0);
          console.log("✅ [ForgingPage] Found values from Playbook_Data:", { cycleTime, machineTonnage, ohpRate });
        }
        
        // Fallback to AssumptionsJson.process_parameters if Playbook_Data not available
        if (cycleTime === 0) {
          if (assumptionsJson?.process_parameters?.total_cycle_time_sec !== undefined) {
            cycleTime = parseNumericValue(assumptionsJson.process_parameters.total_cycle_time_sec, 0);
            console.log("✅ [ForgingPage] Found total_cycle_time_sec from AssumptionsJson.process_parameters:", cycleTime);
          } else if (assumptionsJson?.process_parameters?.cycle_time !== undefined) {
            cycleTime = parseNumericValue(assumptionsJson.process_parameters.cycle_time, 0);
          } else if (assumptionsJson?.process_parameters?.cycleTime !== undefined) {
            cycleTime = parseNumericValue(assumptionsJson.process_parameters.cycleTime, 0);
          }
        }
        
        if (machineTonnage === 0) {
          if (assumptionsJson?.process_parameters?.total_installed_tonnage_ton !== undefined) {
            machineTonnage = parseNumericValue(assumptionsJson.process_parameters.total_installed_tonnage_ton, 0);
            console.log("✅ [ForgingPage] Found total_installed_tonnage_ton from AssumptionsJson.process_parameters:", machineTonnage);
          } else if (assumptionsJson?.process_parameters?.machine_tonnage !== undefined) {
            machineTonnage = parseNumericValue(assumptionsJson.process_parameters.machine_tonnage, 0);
          } else if (assumptionsJson?.process_parameters?.tonnage !== undefined) {
            machineTonnage = parseNumericValue(assumptionsJson.process_parameters.tonnage, 0);
          }
        }
        
        // Fallback to formulaResult if not found
        if (formulaResult) {
          if (cycleTime === 0) {
            cycleTime = 
              formulaResult.total_cycle_time_sec ||
              formulaResult.totalCycleTimeSec ||
              formulaResult.CycleTime ||
              formulaResult.cycleTime ||
              0;
          }
          
          if (machineTonnage === 0) {
            machineTonnage = 
              formulaResult.total_installed_tonnage_ton ||
              formulaResult.totalInstalledTonnageTon ||
              formulaResult.MachineTonnage ||
              formulaResult.machineTonnage ||
              formulaResult.Tonnage ||
              0;
          }
        }
        
        // Extract OHP rate (overhead percentage)
        if (ohpRate === 0) {
          // Try from overhead_profit section
          if (assumptionsJson?.overhead_profit?.factory_overhead_percent !== undefined) {
            ohpRate = parseNumericValue(assumptionsJson.overhead_profit.factory_overhead_percent, 0);
            console.log("✅ [ForgingPage] Found factory_overhead_percent from AssumptionsJson.overhead_profit:", ohpRate);
          } else if (assumptionsJson?.ohpRate !== undefined) {
            ohpRate = parseNumericValue(assumptionsJson.ohpRate, 0);
            // Convert to percentage if it's a decimal (e.g., 0.15 -> 15)
            if (ohpRate < 1 && ohpRate > 0) {
              ohpRate = ohpRate * 100;
            }
          } else if (assumptionsJson?.OHPRate !== undefined) {
            ohpRate = parseNumericValue(assumptionsJson.OHPRate, 0);
            if (ohpRate < 1 && ohpRate > 0) {
              ohpRate = ohpRate * 100;
            }
          } else if (formulaResult?.ohpRate !== undefined) {
            ohpRate = parseNumericValue(formulaResult.ohpRate, 0);
            if (ohpRate < 1 && ohpRate > 0) {
              ohpRate = ohpRate * 100;
            }
          } else if (formulaResult?.OHPRate !== undefined) {
            ohpRate = parseNumericValue(formulaResult.OHPRate, 0);
            if (ohpRate < 1 && ohpRate > 0) {
              ohpRate = ohpRate * 100;
            }
          }
        }

        // Initialize or update PlayBook data
        const existingCostParams = playBookData?.costParams || {};
        const isFirstRun = !playBookData || !playBookData.costParams;
        
        const playbookFromResult = {
          costParams: {
            // On first run, always use extracted values
            // On subsequent runs, use extracted value if current is 0 (not yet set)
            cycleTime: (isFirstRun || existingCostParams.cycleTime === 0) 
              ? parseNumericValue(cycleTime, 0) 
              : existingCostParams.cycleTime,
            machineTonnage: (isFirstRun || existingCostParams.machineTonnage === 0) 
              ? parseNumericValue(machineTonnage, 0) 
              : existingCostParams.machineTonnage,
            ohpRate: (isFirstRun || existingCostParams.ohpRate === 0) 
              ? parseNumericValue(ohpRate, 0) 
              : existingCostParams.ohpRate,
          },
        };
        
        console.log("🔧 [ForgingPage] Setting playbook data from streaming response:", {
          isFirstRun,
          playbookFromResult,
          existingCostParams,
          playbookDataFromResponse: playbookDataFromResponse ? {
            hasCycleTime: playbookDataFromResponse.costParams?.cycleTime !== undefined,
            hasTonnage: playbookDataFromResponse.costParams?.machineTonnage !== undefined,
            hasOhpRate: playbookDataFromResponse.costParams?.ohpRate !== undefined,
          } : null,
          assumptionsJson: assumptionsJson ? {
            process_parameters: assumptionsJson.process_parameters,
            overhead_profit: assumptionsJson.overhead_profit,
            hasTotalCycleTime: assumptionsJson.process_parameters?.total_cycle_time_sec !== undefined,
            hasTotalTonnage: assumptionsJson.process_parameters?.total_installed_tonnage_ton !== undefined,
            hasOverheadPercent: assumptionsJson.overhead_profit?.factory_overhead_percent !== undefined,
          } : null,
          extractedValues: {
            cycleTime,
            machineTonnage,
            ohpRate,
          },
        });
        
        setPlayBookData(playbookFromResult);
        setOldPlayBookData(playbookFromResult);

        if (costResult.riskData) {
          setRiskData(costResult.riskData);
        }
        if (costResult.substituteData) {
          setSubstituteData(costResult.substituteData);
        }
        if (!costResult.riskData || !costResult.substituteData) {
          await fetchRiskAndSubstituteData(
            productData,
            playbookFromResult || playBookData,
            transformedCostData.breakdown
          );
        }

        // Navigate to results section
      setActiveSection("results");
        setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

        // Set key insights from streaming response if available, otherwise fetch separately
        const insights = costResult.keyInsights || null;
        if (insights) {
          console.log("✅ Key insights from streaming response:", insights);
          // Normalize the insights to ensure proper array structure
          const normalizedInsights = {
            summary: insights.summary || "",
            cost_savings: Array.isArray(insights.cost_savings) 
              ? insights.cost_savings 
              : (insights.cost_savings ? [insights.cost_savings] : []),
            strategic: Array.isArray(insights.strategic) 
              ? insights.strategic 
              : (insights.strategic ? [insights.strategic] : []),
          };
          console.log("📊 Normalized key insights:", normalizedInsights);
          console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
          console.log("   - Strategic count:", normalizedInsights.strategic.length);
          setKeyInsightsData(normalizedInsights);
        } else {
          // Fallback: try to fetch key insights separately if not in response
          await fetchKeyInsights(productData, playbookFromResult || {});
        }
      } else {
        throw new Error(costResult.message || "Forging costing failed");
      }
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to generate quick cost: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }


  }

  // const handleQuickCost_Pe = async () => {
  //   if (!productData) return;

  //   setLoadingMessage("Generating Quick Cost...");
  //   setIsProcessing(true);
  //   setKeyInsightsData(null);

  //   try {
  //     // Call stored procedure for plastics costing
  //     console.log("📞 Calling stored procedure with:", { productData, playBookData });

  //     const spResult = await callStoredProcedure(productData, playBookData);

  //     if (spResult && Array.isArray(spResult) && spResult.length > 0) {
  //       const resultData = spResult[0];
        
  //       // Helper function to format numbers to 4 decimal places
  //       const to4 = (n) => {
  //         if (n === null || n === undefined || n === '') return 0;
  //         if (typeof n === 'string') {
  //           const cleaned = n.replace(/[$,\s]/g, '').trim();
  //           const parsed = parseFloat(cleaned);
  //           return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
  //         }
  //         return Number(Number(n).toFixed(4));
  //       };
        
  //       // Map stored procedure output to Analysis Report cost breakdown
  //       // Material = RMPrice: 1.18
  //       // Process = MfrCost: 0.1886
  //       // OHP = oHP: 0.0884
  //       const material = to4(resultData.RMPrice || resultData.Material || resultData.material || resultData.MaterialCost || 0);
  //       const process = to4(resultData.MfrCost || resultData.Manufacturing || resultData.manufacturing || resultData.ProcessCost || 0);
  //       const ohp = resultData.oHP !== null && resultData.oHP !== undefined 
  //         ? to4(resultData.oHP) 
  //         : to4(resultData.OHP || resultData.OHPCost || 0);
        
  //       // Calculate total: material + process + ohp
  //       const calculatedTotal = Number(material) + Number(process) + Number(ohp);
  //       const total = to4(calculatedTotal);
        
  //       // Build assumptions array - prioritize PlayBook values if they exist (user-modified), otherwise use stored procedure results
  //       const costParams = playBookData?.costParams || {};
  //       const assumptionsArray = [];
        
  //       // Part Dimensions - from product data
  //       const dimensionData = productData?.partWeightDimensions || {};
  //       if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
  //         assumptionsArray.push(
  //           `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
  //         );
  //       }
        
  //       // Machine Tonnage - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.machineTonnage) {
  //         assumptionsArray.push(`Machine tonnage: ${costParams.machineTonnage}`);
  //       } else if (resultData.Tonnage || resultData.cTonnage) {
  //         assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
  //       }
        
  //       // Cavities - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.cavities) {
  //         assumptionsArray.push(`Cavities: ${costParams.cavities}`);
  //       } else if (resultData.cCavities || resultData.Cavities) {
  //         assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
  //       }
        
  //       // Runner Type - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.runnerType && costParams.runnerType !== "N/A") {
  //         assumptionsArray.push(`Runner type: ${costParams.runnerType}`);
  //       } else if (resultData.RunnerType) {
  //         assumptionsArray.push(`Runner type: ${resultData.RunnerType}`);
  //       }
        
  //       // Cycle Time - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.cycleTime) {
  //         assumptionsArray.push(`Cycle time: ${costParams.cycleTime}s`);
  //       } else if (resultData.CycleTime) {
  //         assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
  //       }
        
  //       // Machine Rate - always from stored procedure (not user-modifiable in PlayBook)
  //       if (resultData.MHR) {
  //         const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
  //         if (!isNaN(machineRate)) {
  //           assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
  //         }
  //       }
        
  //       // Material Price - always from stored procedure (not user-modifiable in PlayBook)
  //       if (resultData.RMPrice) {
  //         const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
  //         if (!isNaN(materialPrice)) {
  //           assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
  //         }
  //       }
        
  //       // Labour Cost - always from stored procedure
  //       if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
  //         const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
  //           ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
  //           : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
  //         if (!isNaN(labourCost)) {
  //           assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
  //         }
  //       }
        
  //       // OHP Rate - use PlayBook value if available, otherwise use stored procedure result
  //       if (costParams.ohpRate !== null && costParams.ohpRate !== undefined && costParams.ohpRate !== 0) {
  //         const ohpRateValue = typeof costParams.ohpRate === 'number' 
  //           ? costParams.ohpRate 
  //           : parseFloat(costParams.ohpRate);
  //         if (!isNaN(ohpRateValue)) {
  //           assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
  //         }
  //       } else if (resultData.oHP !== null && resultData.oHP !== undefined) {
  //         const ohpRate = typeof resultData.oHP === 'number' ? resultData.oHP : parseFloat(resultData.oHP);
  //         if (!isNaN(ohpRate)) {
  //           // Convert decimal to percentage if needed
  //           const ohpPercent = ohpRate > 1 ? ohpRate : ohpRate * 100;
  //           assumptionsArray.push(`OHP rate: ${ohpPercent.toFixed(2)}%`);
  //         }
  //       }
        
  //       // If no assumptions, add default message
  //       if (assumptionsArray.length === 0) {
  //         assumptionsArray.push("Costing calculated using stored procedure QC_SP_Plastics.");
  //       }
        
  //       const baseOhpRatePercent =
  //         material + process > 0 ? (ohp / (material + process)) * 100 : 0;

  //       // Transform stored procedure result to format expected by ResultsSection
  //       const transformedCostData = {
  //         summary: `Should cost analysis for ${resultData.PartName || resultData.partName || "plastic part"} manufactured in ${resultData.MfrCountry || resultData.mfrCountry || "location"}. Total cost: $${total} per unit.`,
  //         breakdown: {
  //           material: material,
  //           process: process,
  //           logistics: 0, // Plastic costing doesn't have logistics
  //           ohp: ohp,
  //           total_should_cost: total,
  //           currency: "USD/unit",
  //         },
  //         line_items: [],
  //         key_insights: [],
  //         assumptions: assumptionsArray, // Pass as array, not string
  //         _source: "stored_procedure",
  //         _originalResult: resultData,
  //         meta: {
  //           baseOhpCost: ohp,
  //           baseOhpRate: baseOhpRatePercent,
  //           currentOhpRate:
  //             costParams.ohpRate !== undefined && costParams.ohpRate !== null
  //               ? costParams.ohpRate
  //               : baseOhpRatePercent,
  //         },
  //       };
        
  //       // If user already modified PlayBook OHP rate, apply client-side adjustment before storing
  //       const currentPlayBookParams = playBookData?.costParams || {};
  //       let finalCostData = transformedCostData;
  //       if (
  //         currentPlayBookParams.ohpRate !== undefined &&
  //         currentPlayBookParams.ohpRate !== null &&
  //         currentPlayBookParams.ohpRate !== 0
  //       ) {
  //         finalCostData = recalculateOHPCost(
  //           currentPlayBookParams.ohpRate,
  //           transformedCostData,
  //           currentPlayBookParams
  //         );
  //       }

  //       // If Machine Tonnage was modified in PlayBook, fetch and update material price
  //       const tonnage = currentPlayBookParams.machineTonnage;
  //       if (tonnage !== undefined && tonnage !== null && tonnage > 0) {
  //         console.log("\n💰 Tonnage detected in PlayBook, fetching new material price...");
  //         console.log("   - Tonnage:", tonnage);
          
  //         try {
  //           const manufacturingCountry = productData.manufacturingCountry || "";
  //           const materialName = productData.materialSelection || null;
  //           const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

  //           if (manufacturingCountry && partWeightG > 0) {
  //             console.log("   - Manufacturing Country:", manufacturingCountry);
  //             console.log("   - Material Name:", materialName);
  //             console.log("   - Part Weight (g):", partWeightG);

  //             const requestPayload = {
  //               tonnage: tonnage,
  //               manufacturingCountry: manufacturingCountry,
  //               materialName: materialName,
  //             };

  //             console.log("   - Fetching material price from API...");
  //             const response = await apiFetch("/api/mech/material-price", {
  //               method: "POST",
  //               headers: { "Content-Type": "application/json" },
  //               body: JSON.stringify(requestPayload),
  //             });

  //             if (response.ok) {
  //               const result = await response.json();
  //               const newMaterialPricePerKg = parseFloat(result.material_price || 0);

  //               console.log("   ✅ New Material Price received:", `$${newMaterialPricePerKg}/kg`);

  //               if (!isNaN(newMaterialPricePerKg) && newMaterialPricePerKg > 0) {
  //                 // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
  //                 // const partWeightKg = parseFloat(partWeightG) / 1000;
  //                 // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
  //                 // Update Material in cost category table with material price per kg directly
  //                 const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

  //                 console.log("   - Using material price per kg directly as material cost");
  //                 console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);

  //                 // Update material cost and total
  //                 const process = to4(finalCostData.breakdown.process || 0);
  //                 const ohp = to4(finalCostData.breakdown.ohp || 0);
  //                 const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

  //                 console.log("   - Process Cost:", `$${process}/unit`);
  //                 console.log("   - OHP Cost:", `$${ohp}/unit`);
  //                 console.log("   - New Total:", `$${newTotal}/unit`);

  //                 // Update assumptions array with new material price
  //                 const updatedAssumptions = finalCostData.assumptions.map((assumption) => {
  //                   if (assumption.includes("Material price:")) {
  //                     return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
  //                   }
  //                   return assumption;
  //                 });

  //                 // If material price wasn't in assumptions, add it
  //                 const hasMaterialPrice = updatedAssumptions.some(a => a.includes("Material price:"));
  //                 if (!hasMaterialPrice) {
  //                   updatedAssumptions.push(`Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`);
  //                 }

  //                 // Update finalCostData with new material cost
  //                 finalCostData = {
  //                   ...finalCostData,
  //                   breakdown: {
  //                     ...finalCostData.breakdown,
  //                     material: newMaterialCost,
  //                     total_should_cost: newTotal,
  //                   },
  //                   summary: finalCostData.summary.replace(
  //                     /\$[\d.]+ per unit/,
  //                     `$${newTotal} per unit`
  //                   ),
  //                   assumptions: updatedAssumptions,
  //                   meta: {
  //                     ...finalCostData.meta,
  //                     materialPricePerKg: newMaterialPricePerKg,
  //                   },
  //                 };

  //                 console.log("   ✅ Material cost updated in cost breakdown");
  //                 console.log("   ✅ Material price updated in assumptions list");
  //               } else {
  //                 console.warn("   ⚠️ Invalid material price received:", result);
  //               }
  //             } else {
  //               const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
  //               console.warn("   ⚠️ Failed to fetch material price:", errorData.error || "Unknown error");
  //             }
  //           } else {
  //             console.warn("   ⚠️ Missing required data for material price update:", {
  //               manufacturingCountry: !!manufacturingCountry,
  //               partWeightG: partWeightG > 0,
  //             });
  //           }
  //         } catch (error) {
  //           console.error("   ❌ Error updating material price:", error);
  //           // Don't block the flow - continue with stored procedure result
  //         }
  //       }

  //       // Store the (possibly adjusted) result as costData
  //       setCostData(finalCostData);
        
  //       // Map stored procedure output to PlayBook costParams
  //       // cavities = cCavities: 2
  //       // cycleTime = CycleTime: 39.71
  //       // machineTonnage = Tonnage: 39
  //       // ohpRate = oHP: 0.0669 (convert from decimal to percentage: 0.0669 * 100 = 6.69)
  //       // runnerType = RunnerType: "Cold runner"
        
  //       // Helper function to parse percentage (handles both decimal and percentage formats)
  //       const parsePercentage = (value) => {
  //         if (value === null || value === undefined || value === "") return 0;
  //         if (typeof value === "string") {
  //           // Remove % sign if present
  //           const cleaned = value.replace(/%/g, "").trim();
  //           const num = parseFloat(cleaned);
  //           // If the number is > 1, assume it's already a percentage, otherwise assume it's a decimal
  //           return isNaN(num) ? 0 : (num > 1 ? num : num * 100);
  //         }
  //         if (typeof value === "number") {
  //           // If the number is > 1, assume it's already a percentage, otherwise assume it's a decimal
  //           return value > 1 ? value : value * 100;
  //         }
  //         return 0;
  //       };

  //       // On first run: map stored procedure results to PlayBook
  //       // On subsequent runs: preserve user-modified PlayBook values, only update missing ones
  //       const existingCostParams = playBookData?.costParams || {};
  //       const updatedCostParams = {
  //         // Preserve user-modified values, or use stored procedure results if not modified
  //         cavities: existingCostParams.cavities !== undefined && existingCostParams.cavities !== null
  //           ? existingCostParams.cavities
  //           : parseInt(resultData.cCavities || resultData.Cavities || resultData.cavities || 0),
  //         cycleTime: existingCostParams.cycleTime !== undefined && existingCostParams.cycleTime !== null
  //           ? existingCostParams.cycleTime
  //           : parseFloat(resultData.CycleTime || resultData.cycleTime || 0),
  //         machineTonnage: existingCostParams.machineTonnage !== undefined && existingCostParams.machineTonnage !== null
  //           ? existingCostParams.machineTonnage
  //           : parseInt(resultData.Tonnage || resultData.cTonnage || resultData.machineTonnage || 0),
  //         ohpRate: existingCostParams.ohpRate !== undefined && existingCostParams.ohpRate !== null && existingCostParams.ohpRate !== 0
  //           ? existingCostParams.ohpRate
  //           : (resultData.oHP !== null && resultData.oHP !== undefined 
  //               ? parsePercentage(resultData.oHP)
  //               : 0),
  //         runnerType: existingCostParams.runnerType !== undefined && existingCostParams.runnerType !== null && existingCostParams.runnerType !== "N/A"
  //           ? existingCostParams.runnerType
  //           : (resultData.RunnerType || resultData.runnerType || "N/A"),
  //       };

  //       // Update playBookData with mapped costParams (preserves user modifications)
  //       const mergedPlayBookState = {
  //         ...(playBookData || {}),
  //         costParams: updatedCostParams,
  //       };
  //       setPlayBookData(mergedPlayBookState);

  //       // Navigate to results section
  //       setActiveSection("results");
  //       setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

  //       await fetchKeyInsights(productData, mergedPlayBookState);
  //     } else {
  //       throw new Error("Stored procedure returned no data");
  //     }
  //   } catch (error) {
  //     console.error("Quick Cost failed:", error);
  //     alert(`Failed to calculate cost: ${error.message}`);
  //   } finally {
  //     setLoadingMessage("");
  //     setIsProcessing(false);
  //   }
  // };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    // console.log(costRows)
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);


  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Mechanical"
          industry="Mechanical"
          commodity="Forging"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height:"92%"}}>
                <ForgingQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  onDemoModeChange={handleDemoModeChange}
                  onReset={handleProductReset}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <ForgingPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate recipe first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    substituteSectionTitle="Forging Recommendations"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: costData?.assumptions || [],
                      productData,
                      formulaResult: costData?.formulaResult,
                      perlColors,
                      perlTypography: theme.custom?.perlTypography || {},
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}
        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
